/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 基本設計シートを表計算のように操作するための純粋な土台。セルの番地・矢印キーでの移動・範囲の正規化・コピー用 TSV の組み立て・貼り付けの解釈を、描画から切り離して持つ
 * @graph-connects none
 */
import type { ChildTicketRow } from '@cortex/domain-product-innovation-lab-basic-design-tree';
import {
  findChildTicketKind,
  findChildTicketKindByLabel,
} from '@cortex/domain-product-innovation-lab-basic-design-tree';

/**
 * キーボードで行き来する列。**種別も含める**。
 *
 * 種別だけ別扱いにすると、矢印キーで表を渡り歩く途中で 1 列だけ飛ばす形になり、
 * 「種別を選びたいときだけマウスに持ち替える」ことになる。値の自由入力は
 * `coerceCellValue` が種別名の一覧で弾くので、文字として貼られても群と色の
 * 対応は壊れない。
 */
export type SheetColumn = 'kind' | 'item' | 'repository' | 'content' | 'point' | 'assignee';

/**
 * 種別を除いた列。セルの中身を文字として描く側（入力欄・候補リスト）は、
 * 種別のメニューを持たないのでこちらで受ける。
 */
export type SheetValueColumn = Exclude<SheetColumn, 'kind'>;

/** 左から右の並び。表の列順と一致させる。 @graph-connects none */
export const SHEET_COLUMNS: readonly SheetColumn[] = [
  'kind',
  'item',
  'repository',
  'content',
  'point',
  'assignee',
];

/** セルの番地。 */
export interface SheetCell {
  /** 行の番号（0 始まり）。 */
  row: number;
  /** 列の番号（`SHEET_COLUMNS` の添字）。 */
  col: number;
}

/** 矩形の選択範囲。 */
export interface SheetRange {
  /** 上端の行。 */
  top: number;
  /** 下端の行。 */
  bottom: number;
  /** 左端の列。 */
  left: number;
  /** 右端の列。 */
  right: number;
}

/** 貼り付け・消去・編集の可否を決めるのに要る、表全体から来る情報。 */
export interface SheetOptions {
  /**
   * pt が自動で決まる行の id。同じ種別の 1 行目だけが自動なので、行だけを見ても
   * 決められない（`autoPointRowIds` で作ったものを渡す）。
   */
  autoPointRowIds: ReadonlySet<string>;
  /** 「項目」の候補。 */
  items: readonly string[];
  /** 「リポジトリ」の候補。 */
  repositories: readonly string[];
  /** 「担当者」の候補（nickname）。 */
  assignees: readonly string[];
}

/**
 * 矢印キーでの移動先。表の端では止まる（行をまたいで折り返さない）。
 * @graph-connects none
 */
export function moveCell(cell: SheetCell, key: string, rowCount: number): SheetCell {
  const lastRow = Math.max(rowCount - 1, 0);
  const lastCol = SHEET_COLUMNS.length - 1;
  switch (key) {
    case 'ArrowUp':
      return { ...cell, row: Math.max(cell.row - 1, 0) };
    case 'ArrowDown':
      return { ...cell, row: Math.min(cell.row + 1, lastRow) };
    case 'ArrowLeft':
      return { ...cell, col: Math.max(cell.col - 1, 0) };
    case 'ArrowRight':
      return { ...cell, col: Math.min(cell.col + 1, lastCol) };
    case 'Home':
      return { ...cell, col: 0 };
    case 'End':
      return { ...cell, col: lastCol };
    default:
      return cell;
  }
}

/**
 * 起点と現在地から矩形を作る。どちらが先でも同じ矩形になる。
 * @graph-connects none
 */
export function normalizeRange(anchor: SheetCell, focus: SheetCell): SheetRange {
  return {
    top: Math.min(anchor.row, focus.row),
    bottom: Math.max(anchor.row, focus.row),
    left: Math.min(anchor.col, focus.col),
    right: Math.max(anchor.col, focus.col),
  };
}

/**
 * セルがその範囲に入っているか。
 * @graph-connects none
 */
export function isInRange(range: SheetRange, row: number, col: number): boolean {
  return row >= range.top && row <= range.bottom && col >= range.left && col <= range.right;
}

/**
 * その行の pt が自動計算かどうか。自動の行は編集・貼り付け・消去の対象から外す
 * （計算で上書きされるので、入れても次の描画で消えて混乱する）。
 *
 * 自動かどうかは**並びの中での位置**で変わる（同じ種別の 2 行目以降は手入力）ので、
 * 行だけでは決められず `options.autoPointRowIds` を見る。
 * @graph-connects none
 */
export function isReadOnlyCell(
  row: ChildTicketRow,
  column: SheetColumn,
  options: SheetOptions,
): boolean {
  return column === 'point' && options.autoPointRowIds.has(row.rowId);
}

/**
 * セルの文字列表現。コピーにも画面表示にも同じ値を使う。
 * @graph-connects none
 */
export function readCell(row: ChildTicketRow, column: SheetColumn): string {
  switch (column) {
    case 'kind':
      return findChildTicketKind(row.kindId)?.label ?? '';
    case 'item':
      return row.item ?? '';
    case 'repository':
      return row.repository ?? '';
    case 'content':
      return row.content;
    case 'point':
      return row.point;
    case 'assignee':
      return row.assignee ?? '';
  }
}

/** 列ごとの候補を引く（候補が無い列は undefined）。 @graph-connects none */
function optionsFor(column: SheetColumn, options: SheetOptions): readonly string[] | undefined {
  if (column === 'item') return options.items;
  if (column === 'repository') return options.repositories;
  if (column === 'assignee') return options.assignees;
  return undefined;
}

/**
 * 貼り付けられた文字列をその列の値として受け取れる形に直す。
 *
 * - 種別は Backlog の子票種別名で照合し、**一覧に無い名前と空文字は捨てる**（どの行も
 *   必ず種別を 1 つ持つので、消去や貼り付けで空にはしない）
 * - 候補を持つ列（項目 / リポジトリ / 担当者）は、**候補に無い文字列を黙って捨てる**。
 *   自由入力を許すと、見た目は入っているのに Backlog へ出せない値が残る
 * - pt は数として読めないものを捨てる
 * - 空文字はどの列でも「消す」の意味にする
 *
 * 受け取れないときは `undefined` を返し、呼び出し側はそのセルを触らない。
 * @graph-connects none
 */
export function coerceCellValue(
  column: SheetColumn,
  raw: string,
  options: SheetOptions,
): string | null | undefined {
  const text = raw.trim();
  // 種別は空にできない（どの行も必ず 1 つ持つ）。一覧に無い名前も受け取らない。
  if (column === 'kind') return findChildTicketKindByLabel(text)?.id;
  if (text === '') return column === 'content' || column === 'point' ? '' : null;
  const candidates = optionsFor(column, options);
  if (candidates !== undefined) {
    const hit = candidates.find((c) => c.toLowerCase() === text.toLowerCase());
    return hit ?? undefined;
  }
  if (column === 'point') {
    return Number.isFinite(Number.parseFloat(text)) ? text : undefined;
  }
  // 内容は改行をスペースに畳む（1 セルに複数行を貼られても表が崩れない）
  return raw.replace(/\r?\n/g, ' ');
}

/**
 * セルへ書き込んだ行を返す（元の行は変えない）。受け取れない値なら元の行をそのまま返す。
 * @graph-connects coerceCellValue [calls] 列ごとの値の受け取り判定
 */
export function writeCell(
  row: ChildTicketRow,
  column: SheetColumn,
  raw: string,
  options: SheetOptions,
): ChildTicketRow {
  if (isReadOnlyCell(row, column, options)) return row;
  const value = coerceCellValue(column, raw, options);
  if (value === undefined) return row;
  switch (column) {
    case 'kind':
      return value === null ? row : { ...row, kindId: value };
    case 'item':
      return { ...row, item: value };
    case 'repository':
      return { ...row, repository: value };
    case 'assignee':
      return { ...row, assignee: value };
    case 'content':
      return { ...row, content: value ?? '' };
    case 'point':
      return { ...row, point: value ?? '' };
  }
}

/**
 * 範囲をタブ区切りにする。表計算へそのまま貼れる形。
 * @graph-connects readCell [calls] セルの文字列表現
 */
export function rangeToTsv(rows: readonly ChildTicketRow[], range: SheetRange): string {
  const lines: string[] = [];
  for (let r = range.top; r <= range.bottom; r += 1) {
    const row = rows[r];
    if (row === undefined) continue;
    const cells: string[] = [];
    for (let c = range.left; c <= range.right; c += 1) {
      const column = SHEET_COLUMNS[c];
      cells.push(column === undefined ? '' : readCell(row, column));
    }
    lines.push(cells.join('\t'));
  }
  return lines.join('\n');
}

/**
 * タブ区切りの貼り付け文字列を升目に開く。末尾の改行は落とす。
 * @graph-connects none
 */
export function parseTsv(text: string): string[][] {
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/\n+$/, '')
    .split('\n')
    .map((line) => line.split('\t'));
}

/**
 * 升目を選択範囲の左上から流し込む。**行は増やさない**（表の外へは落とす）。
 *
 * 選択が升目より広いときは、升目を**繰り返して選択いっぱいに敷き詰める**。
 * 表計算と同じ振る舞いで、1 セルのコピーを何行にも配れるのと同じように、
 * 2 列ぶんコピーして行を何行か選んでも全行に配れる（列数だけを頼りに
 * 「1 セルのときだけ敷き詰める」としていたため、複数列のコピーが
 * 1 行目にしか入らなかった）。
 *
 * 逆に選択が升目より狭いときは、升目の大きさぶんだけ流し込む（選んだ 1 セルを
 * 起点に、コピーした形のまま入る）。
 * @graph-connects writeCell [calls] セルごとの書き込み
 */
export function applyPaste(
  rows: readonly ChildTicketRow[],
  range: SheetRange,
  matrix: readonly (readonly string[])[],
  options: SheetOptions,
): ChildTicketRow[] {
  const blockHeight = matrix.length;
  const blockWidth = blockHeight === 0 ? 0 : Math.max(...matrix.map((line) => line.length));
  if (blockHeight === 0 || blockWidth === 0) return [...rows];
  const height = Math.max(range.bottom - range.top + 1, blockHeight);
  const width = Math.max(range.right - range.left + 1, blockWidth);
  const next = [...rows];
  for (let dr = 0; dr < height; dr += 1) {
    const r = range.top + dr;
    const row = next[r];
    if (row === undefined) break;
    let written = row;
    for (let dc = 0; dc < width; dc += 1) {
      const c = range.left + dc;
      const column = SHEET_COLUMNS[c];
      if (column === undefined) break;
      // 升目を繰り返して当てる（選択が升目ちょうどなら余りが出ないので素直に 1 対 1）
      const raw = matrix[dr % blockHeight]?.[dc % blockWidth] ?? '';
      written = writeCell(written, column, raw, options);
    }
    next[r] = written;
  }
  return next;
}

/**
 * 範囲のセルを空にする（自動計算の pt は触らない）。
 * @graph-connects writeCell [calls] セルごとの書き込み
 */
export function clearRange(
  rows: readonly ChildTicketRow[],
  range: SheetRange,
  options: SheetOptions,
): ChildTicketRow[] {
  const next = [...rows];
  for (let r = range.top; r <= range.bottom; r += 1) {
    const row = next[r];
    if (row === undefined) continue;
    let written = row;
    for (let c = range.left; c <= range.right; c += 1) {
      const column = SHEET_COLUMNS[c];
      if (column === undefined) continue;
      written = writeCell(written, column, '', options);
    }
    next[r] = written;
  }
  return next;
}
